import { momentumText } from "../format";

/**
 * Templated wording for each insight kind: a claim (the headline) and a
 * why (one sentence of numbers). Deterministic — every number and name in
 * the text comes from the detection, nothing is added. A tenant's copy
 * overrides (zone names, kind names, nouns) flow in through `copy`.
 */

const signed = (n, digits = 1) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(digits)}`;
const pct = (share) => `${Math.round(share * 100)}%`;
const plural = (n, one, many) => (n === 1 ? one : many);

export const phraseInsight = (c, { zones, copy, meta, titleByContentType = {}, displayOf = (v) => v }) => {
  const { nouns } = copy;
  const obs = (n) => `${n} ${plural(n, nouns.observation, nouns.observations)}`;
  const convs = (n) => `${n} ${plural(n, nouns.conversation, nouns.conversations)}`;
  const periods = (n) => `${n} ${plural(n, nouns.period, nouns.periods)}`;
  const dsName = (ds) => titleByContentType[ds] || ds;
  const mom = (item) => momentumText(item, meta).text;
  const s = c.subject;
  const N = c.numbers;

  switch (c.kind) {
    case "caughtFire":
      return {
        claim: `${s.label} ${copy.verbs.caughtFire} in ${meta.nowLabel}`,
        why: `${capitalise(mom(s))}, ${obs(N.n)} across ${convs(N.docs)}${N.prevZone ? `, up from ${zones[N.prevZone].label}` : ""}.`,
      };
    case "zoneMove":
      return {
        claim: `${s.label} moved from ${zones[N.fromZone].label} to ${zones[N.toZone].label}`,
        why: `${capitalise(mom(s))}; severity ${s.severity.value.toFixed(2)}, ${obs(s.n)} across ${convs(s.severity.docs)}.`,
      };
    case "resolving":
      return {
        claim: `${s.label} has been cooling for ${periods(N.periodsCooling)} running`,
        why: `Still severe (${s.signal?.display || "unknown signal"}, ${s.severity.value.toFixed(2)}) but shrinking since ${N.since}. ${copy.prompts.resolving}`,
      };
    case "goneSilent":
      return {
        claim: `${s.label} has gone silent${N.lastActive ? ` since ${N.lastActive}` : ""}`,
        why: `${zones[N.peakZone].label} for ${periods(N.loudPeriods)}, then no ${nouns.observations} for the last ${periods(N.silentPeriods)}. ${copy.prompts.goneSilent}`,
      };
    case "concentration": {
      const topic = c.topic.label;
      const byZone = N.highShare >= N.topShare;
      return {
        claim: byZone
          ? `${topic}: ${N.highCount} of ${N.rowCount} ${nouns.subTopics} sit in ${zones.fires.label} or ${zones.chronic.label}`
          : `${topic} is mostly ${N.top.label}`,
        why: `${N.top.label} alone is ${obs(N.top.n)} across ${convs(N.top.severity.docs)}, ${pct(N.topShare)} of the topic's ${N.total}.`,
      };
    }
    case "channelContrast": {
      const a = N.rising;
      const b = N.other;
      return {
        claim: `${s.label} is rising in ${dsName(a.dataset)} but ${b.band === "cooling" ? "cooling" : "flat"} in ${dsName(b.dataset)}`,
        why: `${obs(a.n)} in ${dsName(a.dataset)} (${mom(a.item)}) against ${obs(b.n)} in ${dsName(b.dataset)} (${mom(b.item)}).`,
      };
    }
    case "escalation":
      return {
        claim: `${c.topic.label} has shifted from ${displayOf(N.from.leading)} to ${displayOf(N.to.leading)} since ${N.from.label}`,
        why: `The topic's leading signal category moved from ${displayOf(N.from.leading)} (${pct(N.negativeShareFrom)} negative in ${N.from.label}) to ${displayOf(N.to.leading)} (${pct(N.negativeShareTo)} negative in ${N.to.label}).`,
      };
    case "breadthDepth":
      return N.variant === "loud"
        ? { claim: `${s.label}: ${obs(N.n)} from ${convs(N.docs)}`, why: copy.prompts.loud }
        : { claim: `${s.label}: ${convs(N.docs)}, one ${nouns.observation} each`, why: copy.prompts.quiet };
    case "winSlipping":
      return {
        claim: `${s.label} is slipping`,
        why: `${capitalise(mom(s))}${N.pair ? `, while ${N.pair.label} stays ${N.pair.zone ? zones[N.pair.zone].label : "severe"}` : ""}.`,
      };
    default:
      return { claim: s?.label || "", why: "" };
  }
};

const capitalise = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
